import { syncHandler } from "@/api/sync-handler";
import { ProjectCard } from "@/components/cards";
import { Can } from "@/components/citadel";
import { useDaily } from "@/components/plan/daily/use-daily";
import { usePlanStore } from "@/components/plan/store";
import { ProjectsFilter, useProjectsFilterController } from "@/components/projects";
import { useMembers } from "@/hooks/use-members";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { AddProjectModal } from "@/modals/projects/add-project-modal";
import { Button } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { Divider } from "@msuite/ui/divider";
import { Droppable, getDroppablePayload } from "@msuite/ui/dnd";
import { useRegisterSelectable } from "@msuite/ui/drag-select";
import { TextInput } from "@msuite/ui/form";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { filterProjectsByWeek } from "@msuite/utils/projects";
import type { Nullable, Tables } from "@msuite/utils/types";
import { mutators, queries } from "@msuite/zero";
import { useZero, useZeroQuery } from "@msuite/zero/react";
import React from "react";
import { TicketActionBar } from "./ticket-action-bar";
import type { TicketAssignmentType } from "./use-crew-handler";

const _hooks = { useRegisterSelectable };

export function ProjectList() {
	const addProjectModal = useDisclosure();
	const activeDate = usePlanStore((state) => state.activeDate);
	const currentWeek = usePlanStore((state) => state.currentWeek);
	const { t } = useTranslation();
	const { members } = useMembers();
	const { workspaces } = useWorkspaces();
	const z = useZero();

	const daily = useDaily(activeDate.db());

	const [projects] = useZeroQuery(queries.projects.list(), {
		ttl: "forever",
	});

	const filteredByWeek = filterProjectsByWeek(projects as any[], currentWeek);

	const projectsFilter = useProjectsFilterController(filteredByWeek, {
		users: members,
		workspaces,
	});

	/**
	 * The project's tickets as the card chain expects them, carrying the organisation the synced
	 * row leaves out. `packages/zero/src/schema/ticket.ts` declares eight columns and `orgId` is
	 * not among them, while every `update Ticket` rule conditions on it — so without this the
	 * card's capability check would refuse everyone but an owner. The project the ticket hangs off
	 * is the ticket's organisation, and this query only asks for the caller's own.
	 */
	function ticketsOf(project: { orgId?: string; _tickets?: unknown[] }) {
		return ((project._tickets ?? []) as Tables<"tickets">[]).map((ticket) => ({
			...ticket,
			orgId: project.orgId,
		})) as Tables<"tickets">[];
	}

	function getTicketColor(ticket: Tables<"tickets">) {
		if (getIsAssigned(ticket)) return "emerald";
		if (getIsSkipped(ticket)) return "amber";
		return null;
	}

	function getIsAssigned(ticket: Tables<"tickets">) {
		const assignments = daily.plan?.crews.flatMap((crew) => crew.ticketAssignments);
		if (assignments?.some((assignment) => assignment.ticketId === ticket.id)) return true;
	}

	function getIsSkipped(ticket: Tables<"tickets">) {
		if (daily.plan?.skippedTickets.includes(ticket.id)) return true;
	}

	function handleOnDropTicketAssignment(ticketAssignmentId: string) {
		syncHandler(z.mutate(mutators.ticketAssignments.delete({ ticketAssignmentId })));
	}

	function handleOnSkipAllTickets(ticketIds: Nullable<string[]>) {
		if (!ticketIds) return;
		const planId = daily?.plan?.id;
		if (!planId) return;
		syncHandler(z.mutate(mutators.plans.batchSkipTickets({ planId, ticketIds })));
	}

	/** Render */
	return (
		<React.Fragment>
			<Droppable
				accept={["ticket-assignment"]}
				className="flex-1 grow-0 h-full min-h-0"
				handleOnDrop={(event) => {
					const payload = getDroppablePayload(event);
					if (payload.type === "ticket-assignment") {
						const item = payload.item as TicketAssignmentType;
						handleOnDropTicketAssignment(item.id);
					}
				}}
			>
				<ContentCard
					title={`${t("projects.projects")} (${projectsFilter.filtered?.length ?? 0})`}
					variant="plain"
					icon={icon("List")}
					className="v-stack overflow-hidden h-full flex-1 min-h-0 scrollbar-hidden pb-0! w-max"
					contentClassName="!pb-0"
					isCollapsible={false}
				>
					<div className="h-stack">
						<TextInput
							control={projectsFilter.searchFormControl}
							name="searchValue"
							label={undefined}
							isRequired={false}
							type="search"
							placeholder={t("placeholders.search")}
						/>
						<ProjectsFilter
							controller={projectsFilter}
							buttonProps={{ size: "sm" }}
						/>
						<Can
							action="create"
							subject="Project"
						>
							<Button
								icon={<Icon name="Plus" />}
								size="sm"
								onClick={addProjectModal.onOpen}
							/>
						</Can>
					</div>
					<Divider
						variant="card"
						className="mt-2"
					/>
					<div
						className="overflow-y-auto overflow-x-hidden scrollbar-hidden bottom-space v-stack pt-3 -mt-2"
						style={{
							width: 500,
						}}
					>
						{projectsFilter.filtered?.map((project) => (
							<ProjectCard
								key={project.id}
								project={project as any}
								tickets={ticketsOf(project)}
								// Dragging a ticket onto a crew is arranging this project's work —
								// `update Plan` or the project relationship, never on a published day.
								isTicketDraggable={daily.canArrangeTicketOf(project.id)}
								ticketProps={{
									leftBorder: "thick",
									getColor: getTicketColor,
									action: (ticket) => (
										<TicketActionBar
											daily={daily}
											ticket={ticket}
										/>
									),
								}}
								contextMenuItems={[
									{
										label: t("planning.mark_all_tickets_as_skipped"),
										icon: () => <div className="size-2 rounded-full bg-yellow-500" />,
										onClick: () => {
											handleOnSkipAllTickets(project._tickets?.map((ticket) => ticket.id));
										},
										// The skip list is a column of the day, so the entry follows the
										// board's rule rather than the project's.
										isDisabled: daily.isInteractionBlocked,
									},
									"divider",
								]}
							/>
						))}
					</div>
				</ContentCard>
			</Droppable>
			{addProjectModal.isOpen && (
				<AddProjectModal
					onClose={addProjectModal.onClose}
					isOpen={addProjectModal.isOpen}
					onComplete={async () => {}}
				/>
			)}
		</React.Fragment>
	);
}
